import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Filter, X } from 'lucide-react';
import type { AdminUserRow } from '@/hooks/useAdminUsers';

export const STEP_LABELS: Record<string, string> = {
  signup: 'Confirmar o cadastro',
  patient_created: 'Cadastrar o paciente',
  care_plan_created: 'Criar o plano de cuidado',
  shift_template_created: 'Configurar a escala',
  team_invited: 'Convidar a equipe',
  team_joined: 'Convite pendente de aceite',
  first_shift: 'Iniciar o primeiro plantão',
  first_task_executed: 'Registrar a primeira tarefa',
  subscribed: 'Escolher um plano',
};

export const STEP_KEYS = Object.keys(STEP_LABELS);

export const ACCOUNT_LABELS: Record<string, string> = {
  b2b_manager: 'Gestor empresa',
  b2b_coordinator: 'Coordenador',
  b2b_caregiver: 'Cuidador empresa',
  b2c_owner: 'Familiar responsável',
  b2c_caregiver: 'Cuidador autônomo',
  observer: 'Observador',
  no_role: 'Sem vínculo',
};

export type InactivityRange = 'all' | '0_3' | '4_7' | '8_14' | '15_30' | '30_plus' | 'never';
export type StuckRange = 'all' | '0_3' | '4_7' | '8_14' | '15_plus';
export type ConfirmedFilter = 'all' | 'yes' | 'no';
export type SubFilter = 'all' | 'free' | 'personal' | 'company';
export type OutreachFilter = 'all' | 'never' | 'sent_no_click' | 'clicked' | 'recent';
export type SortKey = 'created_at' | 'days_inactive' | 'days_stuck' | 'full_name';

export interface LeadFilterState {
  text: string;
  steps: string[];
  inactivity: InactivityRange;
  stuck: StuckRange;
  accountTypes: string[];
  subscription: SubFilter;
  confirmed: ConfirmedFilter;
  outreach: OutreachFilter;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
}

export const DEFAULT_LEAD_FILTERS: LeadFilterState = {
  text: '',
  steps: [],
  inactivity: 'all',
  stuck: 'all',
  accountTypes: [],
  subscription: 'all',
  confirmed: 'all',
  outreach: 'all',
  sortKey: 'created_at',
  sortDir: 'desc',
};

const INACTIVITY_OPTIONS: Array<{ key: InactivityRange; label: string }> = [
  { key: 'all', label: 'Qualquer inatividade' },
  { key: '0_3', label: 'Ativo (0-3 dias)' },
  { key: '4_7', label: '4 a 7 dias' },
  { key: '8_14', label: '8 a 14 dias' },
  { key: '15_30', label: '15 a 30 dias' },
  { key: '30_plus', label: 'Mais de 30 dias' },
  { key: 'never', label: 'Nunca acessou' },
];

const STUCK_OPTIONS: Array<{ key: StuckRange; label: string }> = [
  { key: 'all', label: 'Qualquer tempo parado' },
  { key: '0_3', label: 'Parado até 3 dias' },
  { key: '4_7', label: 'Parado 4 a 7 dias' },
  { key: '8_14', label: 'Parado 8 a 14 dias' },
  { key: '15_plus', label: 'Parado 15 dias ou mais' },
];

const SUB_OPTIONS: Array<{ key: SubFilter; label: string }> = [
  { key: 'all', label: 'Qualquer assinatura' },
  { key: 'free', label: 'Sem assinatura (free)' },
  { key: 'personal', label: 'Assinante familiar' },
  { key: 'company', label: 'Assinante empresa' },
];

const CONFIRMED_OPTIONS: Array<{ key: ConfirmedFilter; label: string }> = [
  { key: 'all', label: 'E-mail: todos' },
  { key: 'yes', label: 'Só e-mail confirmado' },
  { key: 'no', label: 'Só e-mail não confirmado' },
];

const OUTREACH_OPTIONS: Array<{ key: OutreachFilter; label: string }> = [
  { key: 'all', label: 'Estímulo: todos' },
  { key: 'never', label: 'Nunca recebeu estímulo' },
  { key: 'sent_no_click', label: 'Recebeu e não clicou' },
  { key: 'clicked', label: 'Recebeu e clicou' },
  { key: 'recent', label: 'Recebeu nos últimos 3 dias' },
];

function inRange(days: number | null | undefined, range: InactivityRange): boolean {
  if (range === 'all') return true;
  if (range === 'never') return days === null || days === undefined;
  if (days === null || days === undefined) return false;
  switch (range) {
    case '0_3': return days <= 3;
    case '4_7': return days >= 4 && days <= 7;
    case '8_14': return days >= 8 && days <= 14;
    case '15_30': return days >= 15 && days <= 30;
    case '30_plus': return days > 30;
    default: return true;
  }
}

function inStuck(days: number | null | undefined, range: StuckRange): boolean {
  if (range === 'all') return true;
  if (days === null || days === undefined) return false;
  switch (range) {
    case '0_3': return days <= 3;
    case '4_7': return days >= 4 && days <= 7;
    case '8_14': return days >= 8 && days <= 14;
    case '15_plus': return days >= 15;
    default: return true;
  }
}

const DAY = 86400000;

export function applyLeadFilters(rows: AdminUserRow[], f: LeadFilterState): AdminUserRow[] {
  const q = f.text.trim().toLowerCase();
  const out = rows.filter((r) => {
    if (q && !(r.email ?? '').toLowerCase().includes(q) && !(r.full_name ?? '').toLowerCase().includes(q)) {
      return false;
    }
    if (f.steps.length && !f.steps.includes(r.blocked_step ?? '')) return false;
    if (!inRange(r.days_inactive, f.inactivity)) return false;
    if (!inStuck(r.days_stuck, f.stuck)) return false;
    if (f.accountTypes.length && !f.accountTypes.includes(r.account_type)) return false;

    const personalActive = ['active', 'trialing'].includes(r.personal_subscription_status ?? '');
    const companyActive = ['active', 'trialing'].includes(r.company_subscription_status ?? '');
    if (f.subscription === 'free' && (personalActive || companyActive)) return false;
    if (f.subscription === 'personal' && !personalActive) return false;
    if (f.subscription === 'company' && !companyActive) return false;

    if (f.confirmed === 'yes' && r.email_confirmed === false) return false;
    if (f.confirmed === 'no' && r.email_confirmed !== false) return false;

    if (f.outreach === 'never' && r.last_outreach_at) return false;
    if (f.outreach === 'sent_no_click' && (!r.last_outreach_at || r.last_outreach_clicked)) return false;
    if (f.outreach === 'clicked' && !r.last_outreach_clicked) return false;
    if (f.outreach === 'recent') {
      if (!r.last_outreach_at) return false;
      if (Date.now() - new Date(r.last_outreach_at).getTime() > 3 * DAY) return false;
    }
    return true;
  });

  const dir = f.sortDir === 'asc' ? 1 : -1;
  out.sort((a, b) => {
    switch (f.sortKey) {
      case 'full_name':
        return dir * (a.full_name ?? a.email ?? '').localeCompare(b.full_name ?? b.email ?? '');
      case 'days_inactive':
        return dir * ((a.days_inactive ?? 9999) - (b.days_inactive ?? 9999));
      case 'days_stuck':
        return dir * ((a.days_stuck ?? -1) - (b.days_stuck ?? -1));
      default:
        return dir * (a.created_at ?? '').localeCompare(b.created_at ?? '');
    }
  });
  return out;
}

export function activeFilterCount(f: LeadFilterState): number {
  let n = 0;
  if (f.text.trim()) n++;
  if (f.steps.length) n++;
  if (f.inactivity !== 'all') n++;
  if (f.stuck !== 'all') n++;
  if (f.accountTypes.length) n++;
  if (f.subscription !== 'all') n++;
  if (f.confirmed !== 'all') n++;
  if (f.outreach !== 'all') n++;
  return n;
}

interface Props {
  value: LeadFilterState;
  onChange: (next: LeadFilterState) => void;
  resultCount: number;
  totalCount: number;
}

function MultiPopover({
  label, options, selected, onToggle,
}: {
  label: string;
  options: Array<{ key: string; label: string }>;
  selected: string[];
  onToggle: (key: string) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant={selected.length ? 'default' : 'outline'}>
          <Filter className="h-3.5 w-3.5 mr-1" />
          {label}
          {selected.length > 0 && <span className="ml-1 text-xs opacity-80">{selected.length}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-2" align="start">
        {options.map((o) => (
          <div key={o.key} className="flex items-center gap-2">
            <Checkbox
              id={`${label}-${o.key}`}
              checked={selected.includes(o.key)}
              onCheckedChange={() => onToggle(o.key)}
            />
            <Label htmlFor={`${label}-${o.key}`} className="text-sm font-normal cursor-pointer">
              {o.label}
            </Label>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function LeadFilters({ value, onChange, resultCount, totalCount }: Props) {
  const set = (patch: Partial<LeadFilterState>) => onChange({ ...value, ...patch });
  const toggleIn = (arr: string[], key: string) =>
    arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];
  const count = activeFilterCount(value);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={value.text}
          onChange={(e) => set({ text: e.target.value })}
          placeholder="Buscar por nome ou e-mail"
          className="max-w-xs"
        />

        <MultiPopover
          label="Etapa travada"
          options={STEP_KEYS.map((k) => ({ key: k, label: STEP_LABELS[k] }))}
          selected={value.steps}
          onToggle={(k) => set({ steps: toggleIn(value.steps, k) })}
        />

        <MultiPopover
          label="Tipo de conta"
          options={Object.entries(ACCOUNT_LABELS).map(([k, l]) => ({ key: k, label: l }))}
          selected={value.accountTypes}
          onToggle={(k) => set({ accountTypes: toggleIn(value.accountTypes, k) })}
        />

        <Select value={value.inactivity} onValueChange={(v) => set({ inactivity: v as InactivityRange })}>
          <SelectTrigger className="w-[190px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {INACTIVITY_OPTIONS.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={value.stuck} onValueChange={(v) => set({ stuck: v as StuckRange })}>
          <SelectTrigger className="w-[200px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STUCK_OPTIONS.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={value.subscription} onValueChange={(v) => set({ subscription: v as SubFilter })}>
          <SelectTrigger className="w-[185px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SUB_OPTIONS.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={value.confirmed} onValueChange={(v) => set({ confirmed: v as ConfirmedFilter })}>
          <SelectTrigger className="w-[200px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CONFIRMED_OPTIONS.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={value.outreach} onValueChange={(v) => set({ outreach: v as OutreachFilter })}>
          <SelectTrigger className="w-[215px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {OUTREACH_OPTIONS.map((o) => (
              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {count > 0 && (
          <Button size="sm" variant="ghost" onClick={() => onChange({ ...DEFAULT_LEAD_FILTERS })}>
            <X className="h-3.5 w-3.5 mr-1" />Limpar filtros
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline">{resultCount} de {totalCount} leads</Badge>
        {count > 0 && <span>{count} filtro(s) ativo(s)</span>}
      </div>
    </div>
  );
}
